import { Link } from 'react-router-dom'
import { CalendarClock, RefreshCw, Wallet, History, ExternalLink, Check } from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'
import { useMySubscriptionDetail, useMyPayments, useStripePortal } from '@/hooks/useBilling'
import { StatusBadge } from '@/components/StatusBadge'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { openExternalLink } from '@/lib/telegram'
import { cn, displayPlanLabel } from '@/lib/utils'

const PROVIDER_LABEL: Record<string, string> = { 
  stripe: 'Tarjeta de Crédito (Stripe)', 
  bizum_manual: 'Transferencia Bizum' 
}

const PLAN_BENEFITS: Record<string, string[]> = {
  FREE: [
    '1 Top Pick diario de alta precisión',
    'Acceso ilimitado a estadísticas públicas',
    'Historial de picks básico',
  ],
  PRO: [
    'Los 3 Top Picks del día completos',
    'Rendimiento y ROI auditado en tiempo real',
    'Explicaciones estadísticas de la IA por pick',
    'Acceso prioritario y sin publicidad',
  ],
  BLACK: [
    // P0.26.1 (Sección F del encargo): "Acceso al motor Resultado Exacto" retirado -- era
    // copia comercial real y alcanzable (pantalla de planes, visible para cualquier
    // cliente) sobre una función que ya no es pública. No se inventa un beneficio
    // sustituto; el resto de la lista sigue siendo real.
    // P0.53 (29/08/2026, Sección 6 del encargo): ELITE eliminado -- nunca fue un plan
    // comprable (`plan_tiers.py` nunca lo mapea), así que "Todo lo del plan Elite" era una
    // referencia a algo que un cliente real nunca pudo tener. BLACK es PRO + lo propio.
    // P0.55 (29/08/2026, Sección 38 del encargo): la lista ahora refleja SOLO features BLACK
    // realmente implementadas y desplegadas -- "Gestión de banca y asignación óptima de
    // Stake" se retira, sonaba a Stake Guidance (Sección 37: explícitamente no construido
    // todavía, no se anuncia como disponible).
    'Todo lo del plan Pro',
    'Decision Journal -- notas sobre tus propias apuestas',
    'Análisis avanzado de tu banca (categoría, rango de cuota, drawdown personal)',
    'Comparador de estrategias con TU propio historial',
    'Inteligencia personalizada basada en tus datos reales',
  ],
}

export function SubscriptionPage() {
  const { data, isLoading } = useSubscription(true)
  const { data: detailData } = useMySubscriptionDetail(true)
  const { data: paymentsData } = useMyPayments(true)
  const stripePortal = useStripePortal()

  if (isLoading || !data) {
    return (
      <div className="p-4 space-y-4">
        <div className="h-10 w-40 bg-panel-elevated/40 rounded-xl animate-pulse" />
        <SkeletonCard />
      </div>
    )
  }

  const subscription = detailData?.subscription ?? null
  const recentPayments = (paymentsData?.payments ?? []).slice(0, 5)
  // P0.56 (Sección 14 del encargo): ADMIN != plan comercial -- Client Web NUNCA muestra
  // "PLAN ADMIN". `displayPlanLabel` ya resuelve ADMIN -> BLACK (misma señal usada en
  // AppHeader/MorePage); una cuenta ADMIN ve aquí la experiencia comercial BLACK completa
  // (beneficios reales de BLACK, nunca copy de panel administrativo). `isAdminAccount`
  // solo alimenta la nota discreta de metadata más abajo, nunca sustituye la tarjeta.
  const isAdminAccount = data.plan === 'ADMIN'
  const currentPlan = displayPlanLabel(data.plan) || 'FREE'
  const benefits = PLAN_BENEFITS[currentPlan] || PLAN_BENEFITS.FREE

  return (
    <div className="p-4 space-y-6">
      
      {/* Cabecera */}
      <div>
        <h1 className="text-lg font-bold text-text">Mi suscripción</h1>
        <p className="text-xs text-text-dim">Administra tu plan de suscripción y método de pago</p>
      </div>

      {/* Plan Activo Card - Stripe/Linear Style */}
      <div className="card-premium-glow relative overflow-hidden space-y-5">
        <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-brand/5 blur-3xl pointer-events-none" />
        
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[9px] font-black uppercase tracking-wider text-text-dim">Plan Contratado</span>
            <h2 className="text-xl font-extrabold text-brand leading-none">
              {currentPlan}
            </h2>
            {isAdminAccount && (
              <span className="inline-block text-[9px] font-bold text-text-dim/70 uppercase tracking-wider">
                Cuenta administrativa
              </span>
            )}
          </div>
          <StatusBadge status={data.status} />
        </div>

        {/* Listado de Beneficios del Plan */}
        <div className="space-y-2.5 pt-3 border-t border-border/40">
          <p className="text-[10px] font-black text-text-muted uppercase tracking-wider">Beneficios Incluidos</p>
          <ul className="space-y-2">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-xs text-text-muted">
                <span className="h-4 w-4 rounded-full bg-brand-soft flex items-center justify-center shrink-0 mt-0.5 border border-brand/20">
                  <Check className="h-2.5 w-2.5 text-brand" />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Detalles de Facturación */}
        <div className="space-y-3 pt-4 border-t border-border/40 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-text-dim font-semibold flex items-center gap-2">
              <CalendarClock className="h-3.5 w-3.5" />
              <span>Vencimiento:</span>
            </span>
            <span className="font-extrabold text-text">
              {data.expires_at ? new Date(data.expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Sin límite (Staff)'}
            </span>
          </div>

          {subscription && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-text-dim font-semibold flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5" />
                  <span>Método de pago:</span>
                </span>
                <span className="font-extrabold text-text">
                  {PROVIDER_LABEL[subscription.provider] ?? subscription.provider}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-dim font-semibold flex items-center gap-2">
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Renovación automática:</span>
                </span>
                <span className={cn(
                  "px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border",
                  subscription.auto_renew 
                    ? "border-brand/40 text-brand bg-brand-soft" 
                    : "border-border text-text-dim bg-panel-elevated"
                )}>
                  {subscription.auto_renew ? 'Activa' : 'No'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Stripe portal button */}
      {subscription?.has_portal && (
        <button
          onClick={() =>
            stripePortal.mutate(undefined, {
              onSuccess: (res) => openExternalLink(res.portal_url),
            })
          }
          disabled={stripePortal.isPending}
          className="w-full btn-secondary-app flex items-center justify-center gap-2 text-xs font-bold disabled:opacity-50"
        >
          <ExternalLink className="h-4 w-4" />
          <span>Gestionar método de pago / cancelar</span>
        </button>
      )}

      {/* Change/Elegir plan link */}
      {data.plan !== 'ADMIN' && (
        <Link
          to="/mas/renovar"
          className="block w-full btn-brand-app text-center text-xs font-bold"
        >
          {data.status === 'active' ? 'Cambiar o renovar plan de pago' : 'Elegir plan premium'}
        </Link>
      )}

      {/* Historial de Pagos */}
      {recentPayments.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-text-dim">
            <History className="h-3.5 w-3.5" /> 
            <span>Historial de transacciones</span>
          </div>
          <div className="card p-0 divide-y divide-border/60 overflow-hidden">
            {recentPayments.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-panel-elevated/20 transition">
                <div className="min-w-0 space-y-0.5">
                  <p className="text-xs font-extrabold text-text">
                    Plan {displayPlanLabel(p.plan)}
                  </p>
                  <p className="text-[10px] text-text-dim font-bold uppercase tracking-wider">
                    {PROVIDER_LABEL[p.provider] || p.provider} · {new Date(p.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs font-black text-text tabular-nums">
                    {p.amount.toFixed(2)} {p.currency.toUpperCase()}
                  </span>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
